import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { AUDIT_LANGS, PROBLEMS, auditSources, summarize, toCsv, toText } from "@/lib/l10n/audit";
import { appSources } from "@/lib/l10n/registry";

/*
 * Admin Panel — Localization Audit, text check (LOCALIZATION_AUDIT_PLAN.md, Part 1,
 * 2026-11-03). Every interface text, the English beside each translation, with
 * missing, untranslated, placeholder, length and informal-address problems flagged.
 * The app texts are read from this build; the backend texts (report PDF, emails,
 * notes) come from /api/admin/l10n-audit/backend-texts. No AI calls, nothing stored.
 */

const SHOW_MAX = 400;
const LANG_NAMES = { "pt-BR": "Portuguese (pt-BR)", "es-419": "Spanish (es-419)", "fr-CA": "French (fr-CA)" };

function download(text, name, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminLocalizationAudit() {
  const [backend, setBackend] = useState(null); // sources, or null while loading
  const [backendError, setBackendError] = useState("");
  const [lang, setLang] = useState("all");
  const [problem, setProblem] = useState("all");
  const [screen, setScreen] = useState("all");
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/l10n-audit/backend-texts")
      .then(({ data }) => setBackend(data.sources || []))
      .catch((ex) => { setBackend([]); setBackendError(formatApiErrorDetail(ex)); });
  }, []);

  const rows = useMemo(() => {
    const all = auditSources([...appSources(), ...(backend || [])]);
    return all.sort((a, b) => a.screen.localeCompare(b.screen) || a.source.localeCompare(b.source));
  }, [backend]);
  const summary = useMemo(() => summarize(rows), [rows]);
  const screens = useMemo(() => [...new Set(rows.map((r) => r.screen))], [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (screen !== "all" && r.screen !== screen) return false;
      const langs = lang === "all" ? AUDIT_LANGS : [lang];
      const found = langs.flatMap((l) => r.problems[l] || []);
      if (onlyProblems && !found.length) return false;
      if (problem !== "all" && !found.includes(problem)) return false;
      if (q && ![r.key, r.en, ...AUDIT_LANGS.map((l) => r[l])].some((t) => String(t ?? "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, lang, problem, screen, onlyProblems, search]);

  const stamp = new Date().toISOString().slice(0, 10);
  const card = "rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 text-sm space-y-3";
  const select = "rounded border border-[color:var(--border-stone)] px-2 py-1 text-xs bg-white";
  const button = "rounded-full border border-[color:var(--brand)] px-4 py-1.5 text-xs font-medium text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10";

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-l10n-audit">
      <AdminNav title="Localization Audit" />
      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <div className={card}>
          <h2 className="text-base font-semibold">Text check</h2>
          <p className="text-[color:var(--text-secondary)]">
            Every interface text in the app, the report PDF and the emails, with each translation beside the English.
            Admin screens are English-only and not included. Help articles keep example interview wording in English on purpose,
            so some &ldquo;Same as English&rdquo; results there are expected.
          </p>
          {backend === null && <p className="text-xs" data-testid="l10n-loading">Loading the backend texts…</p>}
          {backendError && (
            <p className="text-xs text-[#9a4534]" data-testid="l10n-backend-error">
              The backend texts couldn&rsquo;t be loaded ({backendError}); only the app texts are checked.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse" data-testid="l10n-summary">
              <thead>
                <tr>
                  <th className="text-left pr-4 py-1">Language</th>
                  {Object.values(PROBLEMS).map((label) => <th key={label} className="px-3 py-1 text-right">{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {AUDIT_LANGS.map((l) => (
                  <tr key={l} className="border-t border-[color:var(--border-stone)]">
                    <td className="pr-4 py-1 font-medium">{LANG_NAMES[l]}</td>
                    {Object.keys(PROBLEMS).map((p) => (
                      <td key={p} className={`px-3 py-1 text-right ${summary.byLang[l][p] ? "text-[#9a4534] font-semibold" : "text-[color:var(--text-secondary)]"}`}>
                        {summary.byLang[l][p]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[color:var(--text-secondary)]">
            {summary.texts} texts checked; {summary.withProblems} with at least one problem.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={button} data-testid="l10n-csv-problems"
              onClick={() => download(toCsv(rows.filter((r) => Object.keys(r.problems).length)), `localization-problems_${stamp}.csv`, "text/csv;charset=utf-8")}>
              Download CSV – problems only
            </button>
            <button type="button" className={button} data-testid="l10n-csv-all"
              onClick={() => download(toCsv(rows), `localization-all-texts_${stamp}.csv`, "text/csv;charset=utf-8")}>
              Download CSV – all texts
            </button>
            <button type="button" className={button} data-testid="l10n-txt-problems"
              onClick={() => download(toText(rows, { onlyProblems: true }), `localization-problems_${stamp}.txt`, "text/plain;charset=utf-8")}>
              Download TXT – problems only
            </button>
            <button type="button" className={button} data-testid="l10n-txt-all"
              onClick={() => download(toText(rows), `localization-all-texts_${stamp}.txt`, "text/plain;charset=utf-8")}>
              Download TXT – all texts
            </button>
          </div>
        </div>

        <div className={card}>
          <div className="flex flex-wrap items-center gap-3">
            <select className={select} value={lang} onChange={(e) => setLang(e.target.value)} data-testid="l10n-filter-lang">
              <option value="all">All languages</option>
              {AUDIT_LANGS.map((l) => <option key={l} value={l}>{LANG_NAMES[l]}</option>)}
            </select>
            <select className={select} value={problem} onChange={(e) => setProblem(e.target.value)} data-testid="l10n-filter-problem">
              <option value="all">All problems</option>
              {Object.entries(PROBLEMS).map(([p, label]) => <option key={p} value={p}>{label}</option>)}
            </select>
            <select className={`${select} max-w-xs`} value={screen} onChange={(e) => setScreen(e.target.value)} data-testid="l10n-filter-screen">
              <option value="all">All screens</option>
              {screens.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} data-testid="l10n-only-problems" />
              Only texts with problems
            </label>
            <input
              type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search text or key"
              className="w-56 rounded border border-[color:var(--border-stone)] px-2 py-1 text-xs" data-testid="l10n-search"
            />
          </div>
          <p className="text-xs text-[color:var(--text-secondary)]" data-testid="l10n-shown-count">
            {shown.length} texts{shown.length > SHOW_MAX ? `; showing the first ${SHOW_MAX} (the downloads have them all)` : ""}.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="l10n-table">
              <thead>
                <tr className="text-left">
                  <th className="py-1 pr-3 w-48">Screen · key</th>
                  <th className="py-1 pr-3">English</th>
                  {(lang === "all" ? AUDIT_LANGS : [lang]).map((l) => <th key={l} className="py-1 pr-3">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {shown.slice(0, SHOW_MAX).map((r) => (
                  <tr key={`${r.source}|${r.key}`} className="border-t border-[color:var(--border-stone)] align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium">{r.screen}</div>
                      <div className="text-[10px] text-[color:var(--text-secondary)] break-all">{r.key}</div>
                      {r.area === "backend" && <div className="text-[10px] text-[color:var(--brand)]">backend</div>}
                    </td>
                    <td className="py-2 pr-3 whitespace-pre-wrap">{r.en ?? <em>(missing)</em>}</td>
                    {(lang === "all" ? AUDIT_LANGS : [lang]).map((l) => (
                      <td key={l} className={`py-2 pr-3 whitespace-pre-wrap ${r.problems[l] ? "bg-[#9a4534]/5" : ""}`}>
                        {r[l] ?? <em>(missing)</em>}
                        {r.problems[l] && (
                          <div className="mt-1 text-[10px] font-semibold text-[#9a4534]">
                            {r.problems[l].map((p) => PROBLEMS[p]).join(" · ")}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
